import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEntry, useDeleteEntry, useUpdateEntry, useRetranscribeEntry } from '../hooks/useApi';
import { AudioPlayer } from '../components/AudioPlayer';
import { useAccount } from '../contexts/AccountContext';

export function EntryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isViewingSharedAccount } = useAccount();
  const { data: entry, isLoading, error } = useEntry(id!);
  const deleteMutation = useDeleteEntry();
  const updateMutation = useUpdateEntry();
  const retranscribeMutation = useRetranscribeEntry();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this entry?')) {
      deleteMutation.mutate(id!, {
        onSuccess: () => {
          navigate('/');
        },
      });
    }
  };

  const handleEditTitle = () => {
    setEditedTitle(entry?.title || '');
    setIsEditingTitle(true);
  };

  const handleSaveTitle = () => {
    if (id && editedTitle !== entry?.title) {
      updateMutation.mutate(
        { id, title: editedTitle },
        {
          onSuccess: () => {
            setIsEditingTitle(false);
          },
        }
      );
    } else {
      setIsEditingTitle(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingTitle(false);
    setEditedTitle('');
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2 text-gray-600">
          <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Loading entry...</span>
        </div>
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
            {error instanceof Error ? error.message : 'Entry not found'}
          </div>
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <h1 className="text-3xl font-bold text-gray-900">Entry Details</h1>
            </div>
            {!isViewingSharedAccount && (
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete Entry'}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="mb-6">
            {!isEditingTitle ? (
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm text-gray-500 mb-1">Title</p>
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {entry.title || 'Untitled Entry'}
                  </h2>
                </div>
                {!isViewingSharedAccount && (
                  <button
                    onClick={handleEditTitle}
                    className="ml-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Edit title"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                )}
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-500 mb-1">Title</p>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter title"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveTitle();
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                  />
                  <button
                    onClick={handleSaveTitle}
                    disabled={updateMutation.isPending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={updateMutation.isPending}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mb-4">
            <p className="text-sm text-gray-500">Recorded on</p>
            <p className="text-lg text-gray-900">{formatDate(entry.createdAt)}</p>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-500">Duration</p>
              <p className="text-lg text-gray-900">{formatDuration(entry.duration)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Transcription Status</p>
              <p className="text-lg text-gray-900 capitalize">{entry.transcriptionStatus}</p>
            </div>
          </div>

          {entry.audioUrl && entry.mediaType === 'audio' && (
            <AudioPlayer
              audioUrl={entry.audioUrl}
              transcript={entry.transcript}
              words={entry.words}
              entryDuration={entry.duration}
            />
          )}

          {entry.mediaType === 'video' && entry.streamUid && entry.streamReady && (
            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-2">Video</p>
              <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                <iframe
                  src={`https://customer-${import.meta.env.VITE_CF_ACCOUNT_HASH}.cloudflarestream.com/${entry.streamUid}/iframe`}
                  style={{
                    border: 'none',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '100%',
                  }}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                  allowFullScreen={true}
                />
              </div>
            </div>
          )}

          {entry.audioUrl && entry.mediaType === 'video' && !entry.streamUid && (
            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-2">Video</p>
              <video
                controls
                className="w-full rounded-lg"
                src={entry.audioUrl}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          )}

          {entry.mediaType === 'video' && entry.transcript && (
            <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Transcript</h3>
              <div className="max-h-64 overflow-y-auto">
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {entry.transcript}
                </p>
              </div>
            </div>
          )}

          {entry.mediaType === 'video' && entry.streamUid && !entry.streamReady && (
            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-2">Video</p>
              <div
                style={{ position: 'relative', paddingTop: '56.25%' }}
                className="bg-gray-100 rounded-lg flex items-center justify-center"
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                  className="flex flex-col items-center space-y-3"
                >
                  <svg className="animate-spin h-12 w-12 text-blue-600" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <p className="text-gray-600 font-medium">Video processing...</p>
                </div>
              </div>
            </div>
          )}

          {!entry.audioUrl && !entry.streamUid && entry.mediaType === 'audio' && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
              Audio file not available
            </div>
          )}
        </div>

        {entry.transcriptionStatus === 'pending' && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
            <p className="font-medium">Transcription pending</p>
            <p className="text-sm mt-1">
              Transcription hasn't started yet. Click below to start it now.
            </p>
            <button
              onClick={() => retranscribeMutation.mutate(entry.id)}
              disabled={retranscribeMutation.isPending}
              className="mt-3 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors"
            >
              {retranscribeMutation.isPending ? 'Starting...' : 'Start Transcription'}
            </button>
          </div>
        )}

        {entry.transcriptionStatus === 'processing' && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-800">
            <div className="flex items-center space-x-3">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <div>
                <p className="font-medium">Transcribing...</p>
                <p className="text-sm mt-1">
                  Your recording is being transcribed. This may take a few moments.
                </p>
              </div>
            </div>
          </div>
        )}

        {entry.transcriptionStatus === 'failed' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
            <p className="font-medium">Transcription failed</p>
            <p className="text-sm mt-1">
              There was an error transcribing your audio. You can still listen to the recording.
            </p>
            <button
              onClick={() => retranscribeMutation.mutate(entry.id)}
              disabled={retranscribeMutation.isPending}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {retranscribeMutation.isPending ? 'Retrying...' : 'Retry Transcription'}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
